/* Overdue correction alerts: a completed invoice was changed and its corrected packing slip is still missing after 24 h.
   Red blinking bell (top bar) + red blinking banner + short sound; admin can stop each one for a while or for good. */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BellRing, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";

const store = { data: null, subs: new Set(), timer: null };
const SEEN = () => { try { return `corr_alert_seen_${JSON.parse(localStorage.getItem("user") || "{}").email || ""}`; } catch { return "corr_alert_seen"; } };

const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.25].forEach((t) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "square"; o.frequency.value = 880; g.gain.value = 0.08;
      o.connect(g); g.connect(ctx.destination); o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.15);
    });
    setTimeout(() => ctx.close(), 800);
  } catch { /* sound not allowed */ }
};

export const reloadCorrectionAlerts = async () => {
  try {
    const d = (await axios.get(`${API}/order-register/corrections/alerts`, { headers: getAuthHeader() })).data;
    const seen = new Set(JSON.parse(localStorage.getItem(SEEN()) || "[]"));
    const fresh = d.rows.filter((r) => !seen.has(`${r.invoice_key}|${r.since}`));
    if (fresh.length && d.prefs.sound) beep();
    localStorage.setItem(SEEN(), JSON.stringify(d.rows.map((r) => `${r.invoice_key}|${r.since}`)));
    store.data = d;
    store.subs.forEach((f) => f(d));
  } catch { /* alerts are optional */ }
};

const useAlerts = () => {
  const [d, setD] = useState(store.data);
  useEffect(() => {
    store.subs.add(setD);
    if (!store.timer) {
      reloadCorrectionAlerts();
      store.timer = setInterval(() => { if (!document.hidden) reloadCorrectionAlerts(); }, 60000);
    }
    return () => { store.subs.delete(setD); if (!store.subs.size) { clearInterval(store.timer); store.timer = null; } };
  }, []);
  return d;
};

export const CorrectionAlertBell = () => {
  const d = useAlerts();
  if (!d?.count || !d.prefs.bell) return null;
  return (
    <button type="button" onClick={() => window.dispatchEvent(new Event("corr-alerts-open"))}
      className="corr-blink relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-600 text-white"
      title={`${d.count} changed completed invoice(s) without a corrected slip for 24 h+`} data-testid="corr-alert-bell">
      <BellRing className="h-5 w-5" />
      <span className="absolute -right-1 -top-1 min-w-4 rounded-full border-2 border-white bg-red-800 px-1 text-[10px] font-bold leading-3" data-testid="corr-alert-count">{d.count}</span>
    </button>
  );
};

const goSlip = () => {
  window.history.pushState({}, "", "/?panel=order_register");
  window.dispatchEvent(new Event("panelchange"));
  window.dispatchEvent(new Event("or-deeplink"));
};

const AlertRow = ({ r, d }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const stop = async (mode) => {
    setBusy(mode);
    try {
      await axios.post(`${API}/order-register/corrections/alerts/${r.invoice_key}/stop`, { mode }, { headers: getAuthHeader() });
      toast.success(mode === "permanent" ? `${r.invoice_key}: alert stopped for good` : `${r.invoice_key}: alert stopped for ${d.snooze_hours} h`);
      await reloadCorrectionAlerts();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not stop the alert"); }
    setBusy("");
  };
  const btn = (mode, label) => (
    <Button size="sm" variant={d.stop_mode === mode ? "default" : "outline"} disabled={!!busy} onClick={() => stop(mode)}
      className={`h-6 px-2 text-[11px] ${d.stop_mode === mode ? "bg-red-700 hover:bg-red-800" : "border-red-300 text-red-800"}`} data-testid={`corr-alert-stop-${mode}-${r.invoice_key}`}>
      {busy === mode && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}{label}</Button>
  );
  return (
    <div className="rounded-md border border-red-200 bg-white px-2 py-1.5" data-testid={`corr-alert-${r.invoice_key}`}>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 font-mono font-bold text-red-800" data-testid={`corr-alert-toggle-${r.invoice_key}`}>
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}{r.invoice_key}</button>
        <span className="rounded bg-red-100 px-1.5 font-semibold text-red-700">{r.hours} h without corrected slip</span>
        <span className="text-slate-600">changed by {r.by_name || r.by_email || "—"}</span>
        <span className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={goSlip} className="h-6 px-2 text-[11px]" data-testid={`corr-alert-open-${r.invoice_key}`}>Upload slip</Button>
          {d.is_admin && <>{btn("temporary", `Stop ${d.snooze_hours} h`)}{btn("permanent", "Stop permanently")}</>}
        </span>
      </div>
      {open && <ul className="mt-1 list-disc pl-6 text-slate-700" data-testid={`corr-alert-changes-${r.invoice_key}`}>
        {(r.changes || []).map((c, i) => <li key={i}>{c}</li>)}</ul>}
    </div>
  );
};

export const CorrectionAlertBanner = () => {
  const d = useAlerts();
  const [hidden, setHidden] = useState(false);
  const [forced, setForced] = useState(false);
  useEffect(() => {
    const f = () => { setHidden(false); setForced(true); window.scrollTo({ top: 0, behavior: "smooth" }); };
    window.addEventListener("corr-alerts-open", f); return () => window.removeEventListener("corr-alerts-open", f);
  }, []);
  if (!d?.count || hidden || !(d.prefs.banner || forced)) return null;
  return (
    <div className="corr-blink-border mb-3 space-y-1.5 rounded-lg border-2 border-red-500 bg-red-50 p-2 text-xs" data-testid="corr-alert-banner">
      <p className="flex items-center gap-1.5 font-semibold text-red-800"><BellRing className="corr-blink-icon h-4 w-4" />
        {d.count} changed completed invoice{d.count > 1 ? "s" : ""} still without a corrected packing slip (24 h+)
        <button type="button" onClick={() => setHidden(true)} className="ml-auto font-normal text-red-700 underline" data-testid="corr-alert-hide">Hide</button></p>
      {d.rows.map((r) => <AlertRow key={r.invoice_key} r={r} d={d} />)}
    </div>
  );
};
